/**
 * Contexto global de estado de plataforma.
 *
 * Centraliza la llamada a /api/platform/impersonate/status en UN único fetch
 * por carga de la app. Evita la duplicación entre App.tsx (botón Consola) y
 * AdminShell (guard de acceso a /admin).
 *
 * Expone:
 *  - isPlatformMember  → el usuario tiene membership de plataforma
 *  - impersonating     → hay impersonación activa (para banner, Fase 3)
 *  - targetUserId      → ID del usuario impersonado (para banner, Fase 3)
 *  - startedAt / expiresAt → metadatos de la sesión de impersonación (Fase 3)
 *  - phase             → estado completo de carga ('loading' | 'no-platform' | 'ready')
 *  - refetch           → re-ejecuta la consulta manualmente (útil post-impersonación)
 *
 * Diseñado para Fase 3: el banner de impersonación consumirá `impersonating`,
 * `targetUserId`, etc. sin necesidad de nuevos fetches.
 *
 * Uso:
 *  1. Montar <PlatformStatusProvider> en main.tsx por encima de las rutas.
 *  2. Consumir con `usePlatformStatus()` en cualquier componente descendiente.
 *
 * Referencia: DESIGN_platform_console.md §1.1, §2.3
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { type ImpersonationStatus, fetchImpersonationStatus } from "./platform-api";

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export type PlatformStatusPhase =
  | { phase: "loading" }
  | { phase: "no-platform" } // 403 — sin membership de plataforma
  | { phase: "ready"; status: ImpersonationStatus };

export interface PlatformStatusContextValue {
  /** Estado completo — discriminated union para consumidores avanzados. */
  state: PlatformStatusPhase;
  /** Shorthand: true si el usuario tiene membership de plataforma. */
  isPlatformMember: boolean;
  /**
   * Metadatos de impersonación activa. null si no hay impersonación o sin membership.
   * Preparado para el banner de Fase 3.
   */
  impersonating: boolean;
  targetUserId: string | null;
  /** Email del usuario impersonado — fuente de verdad para el banner (Fase 3). */
  targetEmail: string | null;
  /** Nombre del usuario impersonado — fuente de verdad para el banner (Fase 3). */
  targetName: string | null;
  startedAt: string | null;
  expiresAt: string | null;
  /** TTL por defecto en segundos — útil para pre-rellenar el formulario de impersonación. */
  defaultTtlSeconds: number | null;
  /** Datos crudos de ImpersonationStatus, null si no es platform member. */
  impersonationStatus: ImpersonationStatus | null;
  /** Re-ejecuta el fetch (útil tras iniciar/detener una impersonación). */
  refetch: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const PlatformStatusContext = createContext<PlatformStatusContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function PlatformStatusProvider({
  children,
  hasSession,
}: {
  children: React.ReactNode;
  hasSession: boolean;
}) {
  const [state, setState] = useState<PlatformStatusPhase>({ phase: "loading" });

  const refetch = useCallback(async () => {
    if (!hasSession) return;
    try {
      const s = await fetchImpersonationStatus();
      if (s === null) {
        setState({ phase: "no-platform" });
      } else {
        setState({ phase: "ready", status: s });
      }
    } catch {
      // Error de red o inesperado — no bloqueamos la app; tratamos como sin poder
      setState({ phase: "no-platform" });
    }
  }, [hasSession]);

  useEffect(() => {
    if (hasSession) {
      void refetch();
    } else {
      // Sin sesión → no-platform para que AdminShell resuelva sin spinner infinito.
      // Cuando el usuario se loguee, hasSession pasará a true y refetch correrá limpio.
      setState({ phase: "no-platform" });
    }
  }, [hasSession, refetch]);

  const value = useMemo<PlatformStatusContextValue>(() => {
    const isPlatformMember = state.phase === "ready";
    const impersonationStatus = isPlatformMember ? state.status : null;

    // Narrowar desde la union tipada de ImpersonationStatus
    const activeImpersonation =
      impersonationStatus?.impersonating === true ? impersonationStatus : null;

    return {
      state,
      isPlatformMember,
      impersonating: impersonationStatus?.impersonating ?? false,
      targetUserId: activeImpersonation?.targetUserId ?? null,
      targetEmail: activeImpersonation?.targetEmail ?? null,
      targetName: activeImpersonation?.targetName ?? null,
      startedAt: activeImpersonation?.startedAt ?? null,
      expiresAt: activeImpersonation?.expiresAt ?? null,
      defaultTtlSeconds: activeImpersonation?.defaultTtlSeconds ?? null,
      impersonationStatus,
      refetch: () => void refetch(),
    };
  }, [state, refetch]);

  return <PlatformStatusContext.Provider value={value}>{children}</PlatformStatusContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook consumidor
// ---------------------------------------------------------------------------

export function usePlatformStatus(): PlatformStatusContextValue {
  const ctx = useContext(PlatformStatusContext);
  if (!ctx) {
    throw new Error(
      "usePlatformStatus debe usarse dentro de <PlatformStatusProvider>. " +
        "Verificá que esté montado en main.tsx por encima de las rutas.",
    );
  }
  return ctx;
}
